import { pxToEm } from 'util/pxToEm';
import { Box, Typography } from '@mui/material';
import { SettingsIcon } from 'components/icons';
import { MENU_ICONS, SideMenuLink } from 'components/ui';
import { Drawer } from 'components/ui/Drawer';
import {
  useIsFullWidth,
  useIsLoggedIn,
  useIsMasterAccount,
  useProfile,
} from 'hooks';
import { MainLayout } from 'layouts/MainLayout';
import Head from 'next/head';
import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Routes } from 'services/routes';
import { Image } from 'components/lib';
import { useGetRole } from 'hooks/useGetRole';
import { Logout } from '@mui/icons-material';
import { useRouter } from 'next/router';
import { useAuthApi } from 'services/api';

export interface ProfilePageLayoutProps {
  children?: ReactNode;
  title?: string;
  active:
    | 'account-information'
    | 'email-preferences'
    | 'security'
    | 'schools'
    | 'payments'
    | 'help'
    | 'admin';
}

export const ProfilePageLayout = ({
  children,
  title = 'CampusLush | Profile',
  active,
}: ProfilePageLayoutProps): ReactElement => {
  const { loading } = useProfile();
  const isLoggedIn = useIsLoggedIn();
  const role = useGetRole();
  const isMasterAccount = useIsMasterAccount();

  const isFullWidth = useIsFullWidth();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const authApi = useAuthApi();
  const router = useRouter();

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const navContent = useMemo(
    () => (
      <Box
        height="calc(100% - 60px)"
        width="100%"
        borderRight={isFullWidth ? '1px solid #dedede' : 'none'}
        display="flex"
        flexDirection="column"
      >
        {isFullWidth && (
          <Typography
            variant="h2"
            color="primary"
            sx={{
              color: theme => theme.palette.text.primary,
              fontSize: pxToEm(22),
              fontWeight: 500,
              p: '48px 20px 7px',
            }}
          >
            Profile
          </Typography>
        )}
        {!loading && (
          <>
            <Box
              component="nav"
              role="navigation"
              p={0}
              display="flex"
              flexDirection="column"
              flex={1}
            >
              {isLoggedIn && (
                <>
                  <SideMenuLink
                    icon={
                      <Image
                        width={24}
                        alt={'Account Information'}
                        src={
                          active === 'account-information'
                            ? MENU_ICONS.account
                            : MENU_ICONS.accountOutline
                        }
                      />
                    }
                    href={Routes.profile()}
                    onClick={closeDrawer}
                    label="Account"
                    active={active === 'account-information'}
                  />
                  <SideMenuLink
                    icon={
                      <Image
                        width={24}
                        alt={'Notifications'}
                        src={
                          active === 'email-preferences'
                            ? MENU_ICONS.notifications
                            : MENU_ICONS.notificationsOutline
                        }
                      />
                    }
                    href={Routes.communicationPreferences()}
                    onClick={closeDrawer}
                    label="Notifications"
                    active={active === 'email-preferences'}
                  />
                  {!loading && !isMasterAccount && (
                    <SideMenuLink
                      icon={
                        <Image
                          width={24}
                          alt={'Security'}
                          src={
                            active === 'security'
                              ? MENU_ICONS.security
                              : MENU_ICONS.securityOutline
                          }
                        />
                      }
                      href={Routes.passwordAndSecurity()}
                      onClick={closeDrawer}
                      label="Security"
                      active={active === 'security'}
                    />
                  )}
                </>
              )}
              <SideMenuLink
                icon={
                  <Image
                    width={24}
                    alt={'Help'}
                    src={
                      active === 'help'
                        ? MENU_ICONS.help
                        : MENU_ICONS.helpOutline
                    }
                  />
                }
                href={Routes.help()}
                onClick={closeDrawer}
                label="Help"
                active={active === 'help'}
              />
              {isMasterAccount && (
                <SideMenuLink
                  icon={<SettingsIcon />}
                  href={Routes.master.default()}
                  onClick={closeDrawer}
                  label="Master Panel"
                />
              )}
              <Box sx={{ flex: 1 }} />
              <Box sx={{ borderTop: '1px solid #dedede', mb: 8 }}>
                <SideMenuLink
                  icon={
                    <Logout />
                  }
                  href={Routes.logIn()}
                  onClick={async () => {
                    await authApi.logOut();
                    await router.push(Routes.logIn());
                  }}
                  label="Log Out"
                />
              </Box>
            </Box>
          </>
        )}
      </Box>
    ),
    [role, isMasterAccount, isLoggedIn, loading, isFullWidth, closeDrawer, active, authApi, router],
  );

  return (
    <MainLayout>
      <Head>
        <title>{title}</title>
      </Head>

      {isFullWidth && (
        <MainLayout.Sidebar side="left" fixed>
          {navContent}
        </MainLayout.Sidebar>
      )}

      {!isFullWidth && (
        <Drawer
          title="Profile"
          onOpen={() => setDrawerOpen(true)}
          onClose={closeDrawer}
          open={isDrawerOpen}
        >
          {navContent}
        </Drawer>
      )}

      <Box mt={isFullWidth ? 0 : 26} display="flex" alignItems="center">
        <Typography
          variant="h2"
          fontSize={{ xs: pxToEm(24), md: pxToEm(30) }}
          fontWeight={500}
        >
          {title}
        </Typography>
      </Box>
      {children}
    </MainLayout>
  );
};
